<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Reports</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Roles</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <h1 class="mb-0">Roles</h1>
              <!-- <button type="button" class="form-close-button" @click="handleClose">
                &times;
              </button> -->
            </div>

            <div class="form-body">
              <!-- Table Controls -->
              <!-- <div class="table-controls d-flex justify-content-between align-items-center"> -->
              <div v-if="!loading" class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm d-flex align-items-center w-auto mx-1"
                        v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search" class="form-control form-control-sm pe-5 search-input"
                        placeholder="Search roles..." />
                    </div>
                  </div>

                  <div class="export-links d-flex">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to
                      PDF</a>
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to
                      Excel</a>
                  </div>
                </div>
              </div>

              <!-- Table -->
              <div v-if="loading" class="loading-state text-center py-5">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading roles...</p>
              </div>

              <div v-else class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Name</th>
                        <th>Description</th>
                        <!-- <th>Action</th> -->
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(role, index) in paginatedItems" :key="index" :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td data-label="S/N" class="serial-no text-center">{{ (currentPage - 1) * pageLength + index + 1
                          }}</td>
                        <td data-label="Name" class="primary-col text-start">{{ role.name }}</td>
                        <td data-label="Description" class="secondary-col text-start">{{ role.description }}</td>
                        <!-- <td data-label="Action" class="text-center action">
                          <router-link :to="{ name: 'EditRole', params: { id: role.id } }"
                            class="btn btn-sm btn-primary">
                            <span class="material-symbols-outlined">edit</span>
                          </router-link>
                        </td> -->
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>


              <!-- Pagination Summary and Actions -->
              <div v-if="!loading" class="pagination-info d-flex justify-content-between align-items-center">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Previous</button>
                    </li>
                    <li v-for="page in getPageNumbers" :key="page" class="page-item"
                      :class="{ active: currentPage === page, disabled: page === '...' }">
                      <button class="page-link" href="#" @click.prevent="page !== '...' && changePage(page)">{{ page
                        }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Next</button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2'
import Cookies from 'js-cookie'
import DashboardLayout from '../layout/DashboardLayout.vue'
import { useUserStore } from '@/stores/user'

import { debounce } from 'lodash'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import '@/assets/css/table.css'

const roles = ref([])
const loading = ref(true)
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const userStore = useUserStore()

const root_api_url = process.env.VUE_APP_API_ROOT_URL

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val,
    currentPage.value = 1;
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})

const fetchRoles = async () => {
  const token = Cookies.get('auth_token')
  loading.value = true
  try {
    const response = await axios.get(`${root_api_url}/auth/role`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    const userBizClientId = userStore.bizClientId
    roles.value = response.data
      .filter(role => role.bizClientId === userBizClientId)
      .map(role => ({
        id: role.id,
        name: role.roleName,
        description: role.description
      }))
  } catch (error) {
    console.error('Failed to fetch roles:', error)
    const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || error?.response?.data?.message || 'Failed to load roles. Please try again.'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    });
  } finally {
    loading.value = false
  }
}

const filteredItems = computed(() => {
  return roles.value.filter(role => {
    const term = debouncedSearch.value.toLowerCase()
    return (
      role.name.toLowerCase().includes(term) ||
      role.description.toLowerCase().includes(term)
    )
  })
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const getPageNumbers = computed(() => {
  const pages = [];
  const delta = 2;
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  pages.push(1); // always show first

  if (current > delta + 2) pages.push('...');

  const start = Math.max(2, current - delta);
  const end = Math.min(total - 1, current + delta);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - delta - 1) pages.push('...');

  pages.push(total); // always show last

  return pages;
});

const exportToPDF = () => {
  const doc = new jsPDF()
  autoTable(doc, {
    head: [['S/N', 'Name', 'Description']],
    body: roles.value.map((role, index) => [index + 1, role.name, role.description])
  })
  doc.save('roles.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Roles')
  // Add header row
  worksheet.addRow(['S/N', 'Name', 'Description'])

  // Add data rows
  roles.value.forEach((role, index) => {
    worksheet.addRow([index + 1, role.name, role.description])
  })

  // Style header row (optional)
  worksheet.getRow(1).font = { bold: true }

  // Generate buffer and trigger download
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'roles.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)

}

// const handleClose = () => {
//   console.log('Close clicked')
// }

onMounted(() => {
  fetchRoles(),
    document.title = "View Roles"
})
</script>
<style scoped>
.text-center.action .btn {
  background-color: #28A745 !important;
  border: none !important;
  border-radius: 0px !important;
}

.material-symbols-outlined {
  font-size: 1.25rem;
}
</style>
